import localforage from 'localforage';

import { CountryCodeEnum } from '@/types/regionTypes';

import {
  APOLLO_CLIENTS,
  initializeApollo,
  initializeSecureApolloClient,
} from '@/graphql/client';
import type {
  OrderAppAddressInput,
  OrderAppCheckoutAddressTitleMetadataUpdateMutation,
  OrderAppCheckoutAddressTitleMetadataUpdateMutationVariables,
  OrderAppCheckoutChannelConfigQuery,
  OrderAppCheckoutChannelConfigQueryVariables,
  OrderAppCheckoutCreateMutation,
  OrderAppCheckoutCreateMutationVariables,
  OrderAppCheckoutDeliveryModeUpdateMutation,
  OrderAppCheckoutDeliveryModeUpdateMutationVariables,
  OrderAppCheckoutDeliveryScheduleMetadataDeleteMutation,
  OrderAppCheckoutDeliveryScheduleMetadataDeleteMutationVariables,
  OrderAppCheckoutDeliveryScheduleMetadataUpdateMutation,
  OrderAppCheckoutDeliveryScheduleMetadataUpdateMutationVariables,
  OrderAppCheckoutGwpUpdateMutation,
  OrderAppCheckoutGwpUpdateMutationVariables,
  OrderAppCheckoutLineBraceletSizeMetadataFragment,
  OrderAppCheckoutLineBraceletSizeMetadataUpdateMutation,
  OrderAppCheckoutLineBraceletSizeMetadataUpdateMutationVariables,
  OrderAppCheckoutLinesAddMutation,
  OrderAppCheckoutLinesAddMutationVariables,
  OrderAppCheckoutLinesDeleteMutation,
  OrderAppCheckoutLinesDeleteMutationVariables,
  OrderAppCheckoutShippingAddressUpdateMutation,
  OrderAppCheckoutShippingAddressUpdateMutationVariables,
  OrderAppError,
  OrderAppGetVoucherQuery,
  OrderAppGiftMetadataDeleteMutation,
  OrderAppGiftMetadataDeleteMutationVariables,
  OrderAppGiftMetadataUpdateMutation,
  OrderAppGiftMetadataUpdateMutationVariables,
  OrderAppQueryVoucherArgs,
} from '@/graphql/orderApp/api';
import {
  CheckoutAddressTitleMetadataUpdateDocument,
  CheckoutChannelConfigDocument,
  CheckoutCreateDocument,
  CheckoutDeliveryModeUpdateDocument,
  CheckoutDeliveryScheduleMetadataDeleteDocument,
  CheckoutDeliveryScheduleMetadataUpdateDocument,
  CheckoutGwpUpdateDocument,
  CheckoutLineBraceletSizeMetadataUpdateDocument,
  CheckoutLinesAddDocument,
  CheckoutLinesDeleteDocument,
  CheckoutShippingAddressUpdateDocument,
  GetVoucherDocument,
  GiftMetadataDeleteDocument,
  GiftMetadataUpdateDocument,
} from '@/graphql/orderApp/api';
import type {
  AddressInput,
  CheckoutBillingAddressUpdateMutation,
  CheckoutBillingAddressUpdateMutationVariables,
  CheckoutCustomerAttachMutation,
  CheckoutCustomerAttachMutationVariables,
  CheckoutDeliveryMethodUpdateMutation,
  CheckoutDeliveryMethodUpdateMutationVariables,
  CheckoutEmailUpdateMutation,
  CheckoutEmailUpdateMutationVariables,
  CheckoutFragment,
  CheckoutLineFragment,
  CheckoutMetadataFragment,
  CheckoutQuery,
  CheckoutQueryVariables,
  CheckoutRefreshQuery,
  CheckoutRefreshQueryVariables,
  LineProductFragment,
  UserCheckoutsQuery,
  UserCheckoutsQueryVariables,
} from '@/graphql/saleor/api';
import {
  CheckoutBillingAddressUpdateDocument,
  CheckoutCustomerAttachDocument,
  CheckoutDeliveryMethodUpdateDocument,
  CheckoutDocument,
  CheckoutEmailUpdateDocument,
  CheckoutRefreshDocument,
  UserCheckoutsDocument,
} from '@/graphql/saleor/api';
import { getDropdownValue } from '@/lib/attributes';
import { CacheKey } from '@/lib/const';
import { updateMetadata } from '@/lib/metadata/api';
import { ORDER_FLOW_METADATA } from '@/lib/metadata/const';
import { restorePaymentMethods } from '@/lib/payment/api';
import { getProductType } from '@/lib/product';
import type { Maybe } from '@/lib/types';
import type { CheckoutState } from '@/states/checkout';

export const checkoutCreate = async (
  opts: OrderAppCheckoutCreateMutationVariables,
): Promise<{
  checkout: CheckoutFragment | null;
  errors: OrderAppError[];
}> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppCheckoutCreateMutation,
    OrderAppCheckoutCreateMutationVariables
  >({
    mutation: CheckoutCreateDocument,
    variables: opts,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    checkout: data?.checkoutCreate?.checkout
      ? ({
          ...data?.checkoutCreate?.checkout,
          user: data?.checkoutCreate?.checkout?.user
            ? {
                ...data?.checkoutCreate?.checkout.user,
                __typename: 'User',
              }
            : null,
          __typename: 'Checkout',
        } as CheckoutFragment)
      : null,
    errors: data?.checkoutCreate?.errors || [],
  };
};

export const checkoutRestore = async ({
  id,
  ...opts
}: Omit<CheckoutQueryVariables, 'id'> & { id?: string }): Promise<
  { checkout: CheckoutFragment | null } & Pick<
    CheckoutState,
    'paymentMethod' | 'orderId' | 'isChangedAfterOrderCreated'
  >
> => {
  const checkoutId =
    id ?? (await localforage.getItem<string>(CacheKey.CheckoutId));
  const orderId = await localforage.getItem<string>(CacheKey.OrderId);
  const isChangedAfterOrderCreated = await localforage.getItem<string>(
    CacheKey.IsChangedAfterOrderCreated,
  );

  if (!checkoutId) {
    return {
      checkout: null,
      paymentMethod: null,
      orderId: '',
      isChangedAfterOrderCreated: false,
    };
  }

  const client = initializeApollo();

  const [{ data }, paymentMethods] = await Promise.all([
    client.query<CheckoutQuery, CheckoutQueryVariables>({
      query: CheckoutDocument,
      variables: {
        id: checkoutId,
        ...opts,
      },
    }),
    restorePaymentMethods({ checkoutId }),
  ]);

  return {
    checkout: data?.checkout ?? null,
    paymentMethod: paymentMethods?.pop() ?? null,
    orderId: orderId ?? '',
    isChangedAfterOrderCreated: !!isChangedAfterOrderCreated,
  };
};

export const checkoutLinesAdd = async (
  opts: OrderAppCheckoutLinesAddMutationVariables,
): Promise<OrderAppError[]> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppCheckoutLinesAddMutation,
    OrderAppCheckoutLinesAddMutationVariables
  >({
    mutation: CheckoutLinesAddDocument,
    variables: opts,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  const errors = data?.checkoutLinesAdd?.errors || [];

  if (errors.length > 0) {
    console.error(
      '[Checkout] Add to bag errors:',
      errors,
      'for checkout id: ',
      opts.id,
    );
  }
  return errors;
};

export const checkoutLinesDelete = async (
  opts: OrderAppCheckoutLinesDeleteMutationVariables,
): Promise<OrderAppError[]> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppCheckoutLinesDeleteMutation,
    OrderAppCheckoutLinesDeleteMutationVariables
  >({
    mutation: CheckoutLinesDeleteDocument,
    variables: opts,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  const errors = data?.checkoutLinesDelete?.errors || [];

  if (errors.length > 0) {
    console.error(
      '[Checkout] Delete from bag errors:',
      errors,
      'for checkout id: ',
      opts.id,
    );
  }

  return errors;
};

export const orderAppCheckoutShippingAddressUpdate = async (
  opts: OrderAppCheckoutShippingAddressUpdateMutationVariables,
  secure = false,
): Promise<OrderAppError[]> => {
  const client = secure ? initializeSecureApolloClient() : initializeApollo();

  const { data } = await client.mutate<
    OrderAppCheckoutShippingAddressUpdateMutation,
    OrderAppCheckoutShippingAddressUpdateMutationVariables
  >({
    mutation: CheckoutShippingAddressUpdateDocument,
    variables: opts,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return data?.checkoutShippingAddressUpdate?.errors || [];
};

export const checkoutBillingAddressUpdate = async (
  opts: CheckoutBillingAddressUpdateMutationVariables,
  secure = false,
): Promise<
  NonNullable<
    CheckoutBillingAddressUpdateMutation['checkoutBillingAddressUpdate']
  >
> => {
  const client = secure ? initializeSecureApolloClient() : initializeApollo();

  const { data, errors } = await client.mutate<
    CheckoutBillingAddressUpdateMutation,
    CheckoutBillingAddressUpdateMutationVariables
  >({
    mutation: CheckoutBillingAddressUpdateDocument,
    variables: opts,
  });

  return {
    checkout: data?.checkoutBillingAddressUpdate?.checkout,
    errors:
      data?.checkoutBillingAddressUpdate?.errors ||
      (errors
        ? errors.map((error) => ({
            __typename: 'CheckoutError' as const,
            code: 'INVALID' as const,
            message: error.message,
            field: null,
          }))
        : []),
  };
};

export const checkoutEmailUpdate = async (
  opts: CheckoutEmailUpdateMutationVariables,
): Promise<NonNullable<CheckoutEmailUpdateMutation['checkoutEmailUpdate']>> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    CheckoutEmailUpdateMutation,
    CheckoutEmailUpdateMutationVariables
  >({
    mutation: CheckoutEmailUpdateDocument,
    variables: opts,
  });

  return {
    checkout: data?.checkoutEmailUpdate?.checkout,
    errors: data?.checkoutEmailUpdate?.errors || [],
  };
};

export const checkoutCustomerAttach = async (
  opts: CheckoutCustomerAttachMutationVariables,
): Promise<
  NonNullable<CheckoutCustomerAttachMutation['checkoutCustomerAttach']>
> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    CheckoutCustomerAttachMutation,
    CheckoutCustomerAttachMutationVariables
  >({
    mutation: CheckoutCustomerAttachDocument,
    variables: opts,
  });

  return {
    checkout: data?.checkoutCustomerAttach?.checkout,
    errors: data?.checkoutCustomerAttach?.errors || [],
  };
};

export const checkoutDeliveryMethodUpdate = async (
  opts: CheckoutDeliveryMethodUpdateMutationVariables,
): Promise<
  NonNullable<
    CheckoutDeliveryMethodUpdateMutation['checkoutDeliveryMethodUpdate']
  >
> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    CheckoutDeliveryMethodUpdateMutation,
    CheckoutDeliveryMethodUpdateMutationVariables
  >({
    mutation: CheckoutDeliveryMethodUpdateDocument,
    variables: opts,
  });

  return {
    checkout: data?.checkoutDeliveryMethodUpdate?.checkout,
    errors: data?.checkoutDeliveryMethodUpdate?.errors || [],
  };
};
export const orderAppDeliveryModeUpdate = async (
  opts: OrderAppCheckoutDeliveryModeUpdateMutationVariables,
): Promise<
  NonNullable<OrderAppCheckoutDeliveryModeUpdateMutation['deliveryModeUpdate']>
> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppCheckoutDeliveryModeUpdateMutation,
    OrderAppCheckoutDeliveryModeUpdateMutationVariables
  >({
    mutation: CheckoutDeliveryModeUpdateDocument,
    variables: opts,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    errors:
      data?.deliveryModeUpdate?.errors.map((item) => ({
        ...item,
        field: '__root__',
      })) || [],
  };
};

export const checkoutRefresh = async ({
  full,
  ...variables
}: CheckoutRefreshQueryVariables & { full?: boolean }) => {
  const client = initializeApollo();

  let data;
  let errors;

  if (full) {
    const result = await client.query<CheckoutQuery, CheckoutQueryVariables>({
      query: CheckoutDocument,
      variables,
      fetchPolicy: 'network-only',
    });
    [data, errors] = [result.data, result.errors];
  } else {
    const result = await client.query<
      CheckoutRefreshQuery,
      CheckoutRefreshQueryVariables
    >({
      query: CheckoutRefreshDocument,
      variables,
      fetchPolicy: 'network-only',
    });

    [data, errors] = [result.data, result.errors];
  }

  return {
    checkout: data?.checkout,
    errors,
  };
};

export const checkoutGiftUpdate = async (
  variables: OrderAppGiftMetadataUpdateMutationVariables,
): Promise<{
  checkout: CheckoutMetadataFragment | null;
  errors: OrderAppError[];
}> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppGiftMetadataUpdateMutation,
    OrderAppGiftMetadataUpdateMutationVariables
  >({
    mutation: GiftMetadataUpdateDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    checkout: data?.updateMetadata?.item as CheckoutMetadataFragment | null,
    errors: data?.updateMetadata?.errors ?? [],
  };
};

export const checkoutGiftDelete = async (
  variables: OrderAppGiftMetadataDeleteMutationVariables,
): Promise<{
  checkout: CheckoutMetadataFragment | null;
  errors: OrderAppError[];
}> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppGiftMetadataDeleteMutation,
    OrderAppGiftMetadataDeleteMutationVariables
  >({
    mutation: GiftMetadataDeleteDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    checkout: data?.deleteMetadata?.item as CheckoutMetadataFragment | null,
    errors: data?.deleteMetadata?.errors ?? [],
  };
};

export const checkoutDeliveryScheduleUpdate = async (
  variables: OrderAppCheckoutDeliveryScheduleMetadataUpdateMutationVariables,
): Promise<{
  checkout: CheckoutMetadataFragment | null;
  errors: OrderAppError[];
}> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppCheckoutDeliveryScheduleMetadataUpdateMutation,
    OrderAppCheckoutDeliveryScheduleMetadataUpdateMutationVariables
  >({
    mutation: CheckoutDeliveryScheduleMetadataUpdateDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    checkout: data?.updateMetadata?.item as CheckoutMetadataFragment | null,
    errors: data?.updateMetadata?.errors ?? [],
  };
};

export const checkoutDeliveryScheduleDelete = async (
  variables: OrderAppCheckoutDeliveryScheduleMetadataDeleteMutationVariables,
): Promise<{
  checkout: CheckoutMetadataFragment | null;
  errors: OrderAppError[];
}> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppCheckoutDeliveryScheduleMetadataDeleteMutation,
    OrderAppCheckoutDeliveryScheduleMetadataDeleteMutationVariables
  >({
    mutation: CheckoutDeliveryScheduleMetadataDeleteDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    checkout: data?.deleteMetadata?.item as CheckoutMetadataFragment | null,
    errors: data?.deleteMetadata?.errors ?? [],
  };
};

export const checkoutLineBraceletSizeUpdate = async (
  variables: OrderAppCheckoutLineBraceletSizeMetadataUpdateMutationVariables,
): Promise<{
  checkout: OrderAppCheckoutLineBraceletSizeMetadataFragment | null;
  errors: OrderAppError[];
}> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppCheckoutLineBraceletSizeMetadataUpdateMutation,
    OrderAppCheckoutLineBraceletSizeMetadataUpdateMutationVariables
  >({
    mutation: CheckoutLineBraceletSizeMetadataUpdateDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    checkout: data?.updateMetadata
      ?.item as OrderAppCheckoutLineBraceletSizeMetadataFragment | null,
    errors: data?.updateMetadata?.errors ?? [],
  };
};

export const checkoutAddressTitleUpdate = async (
  variables: OrderAppCheckoutAddressTitleMetadataUpdateMutationVariables,
): Promise<{
  checkout: CheckoutMetadataFragment | null;
  errors: OrderAppError[];
}> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppCheckoutAddressTitleMetadataUpdateMutation,
    OrderAppCheckoutAddressTitleMetadataUpdateMutationVariables
  >({
    mutation: CheckoutAddressTitleMetadataUpdateDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return {
    checkout: data?.updateMetadata?.item as CheckoutMetadataFragment | null,
    errors: data?.updateMetadata?.errors ?? [],
  };
};

export const checkoutGwpUpdate = async (
  variables: OrderAppCheckoutGwpUpdateMutationVariables,
): Promise<OrderAppError[]> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppCheckoutGwpUpdateMutation,
    OrderAppCheckoutGwpUpdateMutationVariables
  >({
    mutation: CheckoutGwpUpdateDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return data?.gwpCheckoutUpdate?.errors ?? [];
};

export const getLineSubheader = (
  product: Maybe<LineProductFragment>,
  sku: Maybe<string>,
) => {
  if (!product) {
    return '';
  }

  const isWatch = getProductType(product.productType) === 'watch';
  const firstAttribute = isWatch ? getDropdownValue(product.caseMaterial) : sku;
  const secondAttribute = getDropdownValue(
    isWatch ? product.color : product.size,
  );
  return [firstAttribute, secondAttribute].filter(Boolean).join(' - ');
};

export const getLinesIdWithNotSelectedBraceletSize = (
  lines?: CheckoutLineFragment[],
) =>
  lines
    ?.filter(
      (line) =>
        line.variant.product.strapColor?.values[0]?.name === 'Metal bracelet' &&
        line.variant.product.productType.slug === 'watch' &&
        !line.relatedBraceletSize,
    )
    .map(({ id }) => id) ?? [];

export const getChannelConfig = async (
  variables: OrderAppCheckoutChannelConfigQueryVariables,
): Promise<OrderAppCheckoutChannelConfigQuery['channelConfig']> => {
  const client = initializeApollo();

  const { data } = await client.query<
    OrderAppCheckoutChannelConfigQuery,
    OrderAppCheckoutChannelConfigQueryVariables
  >({
    query: CheckoutChannelConfigDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return data?.channelConfig;
};

export const setRegisterMyWatchChecked = async (
  checkoutId: string,
  value: boolean,
) => {
  const input = [
    {
      key: ORDER_FLOW_METADATA.wasRegisterMyWatchChecked,
      value: value ? 'true' : 'false',
    },
  ];

  await updateMetadata({
    id: checkoutId,
    input,
  });
};

export const getVoucherType = async (variables: OrderAppQueryVoucherArgs) => {
  const client = initializeApollo();

  const { data } = await client.query<
    OrderAppGetVoucherQuery,
    OrderAppQueryVoucherArgs
  >({
    query: GetVoucherDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return data;
};

export const isJapaneseAddress = (
  address: OrderAppAddressInput | AddressInput,
): boolean => {
  return address.country === CountryCodeEnum.JP;
};

export const isEmptyString = (value?: string | null): boolean => {
  return !value || value.trim() === '';
};

export const formatAddressForCountry = ({
  address,
  isStreetAddress2SameAsCity = false,
}: {
  address: OrderAppAddressInput | AddressInput;
  isStreetAddress2SameAsCity?: boolean;
}): OrderAppAddressInput | AddressInput => {
  const isJP = isJapaneseAddress(address);
  const cityIsEmpty = isEmptyString(address.city);

  let cityValue = address.city;
  if (isJP && cityIsEmpty) {
    cityValue = address.streetAddress2;
  }

  let streetAddress2Value = address.streetAddress2;
  if (isJP) {
    streetAddress2Value = isStreetAddress2SameAsCity ? cityValue : '';
  }

  return {
    ...address,
    city: cityValue,
    streetAddress2: streetAddress2Value,
  };
};

export const getUserCheckouts = async (
  variables: UserCheckoutsQueryVariables,
) => {
  const client = initializeSecureApolloClient();

  const { data } = await client.query<
    UserCheckoutsQuery,
    UserCheckoutsQueryVariables
  >({
    query: UserCheckoutsDocument,
    variables,
  });

  return data?.checkouts;
};
